import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../utils/axios';

// Paso 1: Crear sesión con PKCE
export interface SessionRequest {
  embedId: string;
  codeChallenge: string;
}

export interface SessionResponse {
  sessionId: string;
  expiresAt: string;
  embedId: string;
}

// Paso 2: Intercambiar por token de acceso
export interface TokenRequest {
  sessionId: string;
  codeVerifier: string;
}

export interface TokenResponse {
  ok: boolean;
  accessToken: string; // El API devuelve accessToken en camelCase
  tokenType: string; // El API devuelve tokenType en camelCase
  expiresIn: string; // El API devuelve expiresIn como string "10m"
}

/**
 * Hook para crear una nueva sesión usando PKCE (Paso 1)
 * Almacena en la DB el codeChallenge y sessionId (válido por 2 minutos)
 * @param embedId - El embed key/ID
 * @param codeChallenge - El PKCE code challenge
 * @param enabled - Si la query debe estar habilitada
 */
export const useCreateSession = (
  embedId: string,
  codeChallenge: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['session', 'create', embedId, codeChallenge],
    queryFn: async (): Promise<SessionResponse> => {
      const response = await axiosInstance.post('/Public/session', {
        embedId,
        codeChallenge
      });
      return response.data;
    },
    enabled: enabled && !!embedId && !!codeChallenge,
    staleTime: 1 * 60 * 1000, // 1 minuto (sesión expira en 2 min)
    gcTime: 2 * 60 * 1000, // 2 minutos
    retry: false, // No retries to avoid multiple requests
  });
};

/**
 * Hook para intercambiar sessionId + codeVerifier por accessToken JWT (Paso 2)
 * Valida que el sessionId sea válido y el codeVerifier también sea válido
 * Retorna accessToken JWT que expira en 10 minutos
 * @param sessionId - El session ID obtenido en el paso 1
 * @param codeVerifier - El PKCE code verifier
 * @param enabled - Si la query debe estar habilitada
 */
export const useExchangeToken = (
  sessionId: string,
  codeVerifier: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['token', 'exchange', sessionId],
    queryFn: async (): Promise<TokenResponse> => {
      const response = await axiosInstance.post('/Public/token', {
        sessionId,
        codeVerifier
      });
      return response.data;
    },
    enabled: enabled && !!sessionId && !!codeVerifier,
    staleTime: 8 * 60 * 1000, // 8 minutos (token expira en 10 min)
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: false, // No retries to avoid multiple requests
  });
};

// Funciones standalone para uso sin hooks
export const createSession = async (embedId: string, codeChallenge: string): Promise<SessionResponse> => {
  const response = await axiosInstance.post('/Public/session', {
    embedId,
    codeChallenge
  });
  return response.data;
};

export const exchangeToken = async (sessionId: string, codeVerifier: string): Promise<TokenResponse> => {
  const response = await axiosInstance.post('/Public/token', {
    sessionId,
    codeVerifier
  });
  return response.data;
};
























